import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ATHENS_GRAY } from "@/shared/colors";
import { ErrorModal, HelmetDexteel, getMomentTz } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Moment } from "moment-timezone";
import { useState } from "react";
import { DelaysParetoGraphic } from "./components/DelaysParetoGraphic";
import { Filters } from "./components/filters/Filters";
import { DelayForPareto } from "./models/DelayForPareto";
import { getDelaysForPareto } from "./repositories/DelaysParetoReportRepository";

const moment = getMomentTz();

type useDelaysParetoProps = {
	startDate: Date;
	endDate: Date;
	delayAreaAssetId?: number | null;
	includeUndeclared: boolean;
	onError: (error: Error) => void;
};

export const useDelaysPareto = ({
	startDate,
	endDate,
	delayAreaAssetId,
	includeUndeclared,
	onError,
}: useDelaysParetoProps) => {
	return useQuery<DelayForPareto[], Error>({
		queryKey: [
			"delaysPareto",
			startDate,
			endDate,
			delayAreaAssetId,
			includeUndeclared,
		],
		queryFn: () =>
			getDelaysForPareto({
				startDate,
				endDate,
				delayAreaAssetId,
				includeUndeclared,
			}),
		onError: onError,
	});
};

export const DelaysParetoReportPage = () => {
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [includeUndeclaredDelays, setIncludeUndeclaredDelays] =
		useState<boolean>(true);

	const [filterStartDate, setFilterStartDate] = useState<Moment>(
		moment().subtract(7, "days").startOf("day"),
	);
	const [filterEndDate, setFilterEndDate] = useState<Moment>(
		moment().endOf("day"),
	);
	const [filterLine, setFilterLine] = useState<number | null>(null);

	// filterLine === null → "All" → SP receives null and skips area filter
	const { data: delaysPareto } = useDelaysPareto({
		startDate: filterStartDate.toDate(),
		endDate: filterEndDate.toDate(),
		delayAreaAssetId: filterLine,
		includeUndeclared: includeUndeclaredDelays,
		onError: (e) => setError(e.message),
	});

	const handleFilterChange = (startDate: Moment, endDate: Moment) => {
		setFilterStartDate(startDate);
		setFilterEndDate(endDate);
	};

	const handleIncludeUndeclaredDelaysChange = (include: boolean) => {
		setIncludeUndeclaredDelays(include);
	};

	return (
		<>
			<HelmetDexteel title="Pareto Report" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				style={{ backgroundColor: ATHENS_GRAY }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Pareto Report
					</Typography>
				</Grid2>
				<Grid2 size={{ xs: "auto" }}>
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<Filters
							onFilterChange={handleFilterChange}
							initialStartDate={filterStartDate}
							initialEndDate={filterEndDate}
							filterLine={filterLine}
							onFilterLineChange={setFilterLine}
							includeUndeclaredDelays={includeUndeclaredDelays}
							onIncludeUndeclaredDelaysChange={
								handleIncludeUndeclaredDelaysChange
							}
						/>
					</Paper>
				</Grid2>

				<Grid2 size={{ md: 12, xs: 12 }}>
					<DelaysParetoGraphic delaysForPareto={delaysPareto || []} />
				</Grid2>
			</Grid2>
			<ErrorModal
				error={error}
				onHide={() => {
					setError("");
				}}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
